import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useDispatch } from "react-redux";
import { includes, isEmpty, isEqual, isUndefined, replace, size } from "lodash"
import { withTranslation } from "react-i18next";

import inventoryOverviewService from "@plannel/services/ip/inventory-overview-service";
import SplitterContainer from "@plannel/components/SplitterContainer";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import ItemTypeFilter, { ITEM_TYPE } from "@plannel/components/filter/ItemTypeFilter";

const CURRENT_DATE = new ZDate();
const INITIAL_START_DATE = new ZDate(CURRENT_DATE.getFullYear() - 1, CURRENT_DATE.getMonth() + 1, 1);
const INITIAL_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const MAX_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INITIAL_PERIOD = { start: INITIAL_START_DATE, end: INITIAL_END_DATE };
const VIEWLIST = { location: "location", category: "realCategory" };

const { snackBarMessageColor } = ColorUtil;

const InventoryOverview = ({ t, viewName, title }) => {
  const reduxDispatch = useDispatch();
  const reduxViewState = reduxUtil.getViewState(viewName);
  // 월별 재고 추이로 가는 드릴 링크 — 그 화면이 이 테넌트/사용자 권한 밖이면 링크로 보이지도, 눌리지도 않게 한다.
  const canDrillMonthlyTrend = useMenuAccess().isPermitted("INV_MON_TREND");
  const cellStyle = canDrillMonthlyTrend ? { color: 'blue', textDecoration: 'underline', cursor: 'pointer' } : null;
  const gridRef = useRef();
  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState(reduxViewState?.selectedView ?? VIEWLIST.location);
  const [overviews, setOverviews] = useState([]);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [itemTypeFilter, setItemTypeFilter] = useState(reduxViewState?.itemType ? reduxViewState.itemType : null);
  const [periodFilter, setPeriodFilter] = useState(
    reduxViewState?.period ?
    { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
    : INITIAL_PERIOD
  );
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [brushStartEndIndex, setBrushStartEndIndex] = useState({ start: null, end: null });

  const currencyMap = useRef();
  const filterValueRef = useRef(filterValue);
  const periodRef = useRef(periodFilter);
  const itemTypeFilterRef = useRef(itemTypeFilter);
  const userData = JSON.parse(localStorage.getItem("user"));

  const cateChartSeries = [
    { id: "skus", field: "skus", text: t("skus"), type: "bar" },
    { id: "avgNoOfLocation", field: "avgNoOfLocation", text: t("avgNoOfLocation"), type: "bar" },
    { id: "avgSalesMonthly", field: "avgSalesMonthly", text: t("avgSalesMonthly"), type: "bar", groupName: t("qty") },
    { id: "avgStockMonthly", field: "avgInvMonthly", text: t("avgStockMonthly"), type: "bar", groupName: t("qty") },
    { id: "totalSales", field: "totalSales", text: t("totalSales"), type: "bar", groupName: t("qty") },    
    { id: "avgSalesMonthlyAmt", field: "avgSalesMonthlyAmt", text: t("avgSalesMonthlyAmt"), type: "bar", isAmt: true, groupName: t("amt") },
    { id: "avgStockMonthlyAmt", field: "avgInvMonthlyAmt", text: t("avgStockMonthlyAmt"), type: "bar", isAmt: true, groupName: t("amt") },
    { id: "totalSalesAmt", field: "totalSalesAmt", text: t("totalSalesAmt"), type: "bar", isAmt: true, groupName: t("amt") },
    { id: "invDays", field: "inventoryDays", text: t("invDays"), type: "bar" },
    { id: "inventoryTurnover", field: "inventoryTurnover", text: t("invTurnover"), type: "line" }
      ];

  const locChartSeries = [
    { id: "skus", field: "skus", text: t("skus"), type: "bar" },
    { id: "avgSalesMonthly", field: "avgSalesMonthly", text: t("avgSalesMonthly"), type: "bar", groupName: t("qty") },
    { id: "avgStockMonthly", field: "avgInvMonthly", text: t("avgStockMonthly"), type: "bar", groupName: t("qty") },
    { id: "totalSales", field: "totalSales", text: t("totalSales"), type: "bar", groupName: t("qty") },    
    { id: "avgSalesMonthlyAmt", field: "avgSalesMonthlyAmt", text: t("avgSalesMonthlyAmt"), type: "bar", isAmt: true, groupName: t("amt") },
    { id: "avgStockMonthlyAmt", field: "avgInvMonthlyAmt", text: t("avgStockMonthlyAmt"), type: "bar", isAmt: true, groupName: t("amt") },
    { id: "totalSalesAmt", field: "totalSalesAmt", text: t("totalSalesAmt"), type: "bar", isAmt: true, groupName: t("amt") },
    { id: "invDays", field: "inventoryDays", text: t("invDays"), type: "bar" },
    { id: "inventoryTurnover", field: "inventoryTurnover", text: t("invTurnover"), type: "line" }
  ];
  
  const chartSeries = useMemo(() => {
    return isEqual(VIEWLIST.location, selectedView) ? locChartSeries : cateChartSeries;

    // 시리즈 텍스트가 t() 라 언어가 바뀌면 다시 만들어야 한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, t])

  const selectedDataSeries = useMemo(() => {
    return isEqual(VIEWLIST.location, selectedView) ? [chartSeries[4], chartSeries[7]] : [chartSeries[4], chartSeries[5]];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, chartSeries])

  const selectXais = useMemo(() => {
    return isEqual(VIEWLIST.location, selectedView) ? "locationName" : "categoryName";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView])
  
  const currencyRenderer = param => {
    const locCd = param?.data?.locationCd;
    let symbol = userData?.currencySymbol;
    if (locCd !== null || !isUndefined(locCd)) {
      if (currencyMap?.current && size(Object.keys(currencyMap?.current))) {
        const currencyCd = currencyMap.current[locCd];
        if (currencyCd) {
          symbol = CodeUtil.getCurrencySymbol(currencyCd);
        }
      }
    } 
    const value = param.data?.[param?.colDef?.field] ?? 0;
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (isUndefined(symbol)) {
      return formatValue;
    }

    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };

  const onCellClicked = params => {
    const { column, data } = params;

    if (!canDrillMonthlyTrend) return;
    if (column && includes(["locationName", "categoryName"], column.colId)) {
      const reduxKey = "INV_MON_TREND";
      if (isEqual(column.colId, "locationName")) {
        const clickedLocation = { locCd: data?.locationCd, name: data?.locationName };
        const viewState = reduxUtil.createObjectState(reduxKey, { filter: { category: filterValueRef.current?.category, locations: [clickedLocation] }, period: periodRef.current, itemType: itemTypeFilterRef.current });
        reduxDispatch(reduxUtil.updateViewState(viewState));
        reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
      } else {
        const { categoryCd, categoryName } = data;
        const filter = {
          category: [{ hrchyCd: categoryCd, name: categoryName, leafFlg: true }],
          locations: filterValueRef.current?.locations
        };
  
        const viewState = reduxUtil.createObjectState(reduxKey, { filter, period: periodRef.current, itemType: itemTypeFilterRef.current });
        reduxDispatch(reduxUtil.updateViewState(viewState));
        reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
      }
    }
  }

  const columnDefs = (view) => {
    const headerName = isEqual(VIEWLIST.location, view) ? "locName" : "categoryName";
    const field = isEqual(VIEWLIST.location, view) ?  "locationName" : "categoryName";
    const columns = isEqual(VIEWLIST.location, selectedView) ? [] 
      : [{ headerName: "locCd", field: "locationCd", type: ["nonEditableColumn"], hide: true, width: 140 },
      { headerName: "avgNoOfLocation", field: "avgNoOfLocation", type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 140 }];

    const locDesc = isEqual(VIEWLIST.location, selectedView) ? [{ headerName: "locDesc", field: "locationDesc", type: ["nonEditableColumn"], pinned: "left", lockPinned: true, hide: true }] : [ ]
    return ([
      ...locDesc,
      {
        headerName, field, type: ["nonEditableColumn"],
        onCellClicked, cellStyle, width: 180, pinned: "left", lockPinned: true, filter: true
      },
      { headerName: "skus", field: "skus", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 140 },
      ...columns,
      { headerName: "avgSalesMonthly", field: "avgSalesMonthly", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 140 },
      { headerName: "avgStockMonthly", field: "avgInvMonthly", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 140 },
      { headerName: "totalSales", field: "totalSales", type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 140 },
      { headerName: "avgSalesMonthlyAmt", field: "avgSalesMonthlyAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellRenderer: currencyRenderer, width: 140 },
      { headerName: "avgStockMonthlyAmt", field: "avgInvMonthlyAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellRenderer: currencyRenderer, width: 140 },
      { headerName: "totalSalesAmt", field: "totalSalesAmt", type: ["numberColumn", "nonEditableColumn", "rightAligned"], cellRenderer: currencyRenderer, width: 140 },
      { headerName: "invDays", field: "inventoryDays", type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 140 },
      { headerName: "invTurnover", field: "inventoryTurnover", type: ["rationalColumn", "nonEditableColumn", "rightAligned"], width: 140 },
    ])
  }

  const getRequestParams = () => {
    const params = {};

    if (itemTypeFilter) {
      params.itemType = itemTypeFilter;
    }

    if (filterValue?.category) {
      params.categoryCd = filterValue?.category?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);
    }

    if (filterValue?.locations) {
      params.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (periodFilter?.start) {
      params.startDate = periodFilter.start;
    }

    if (periodFilter?.end) {
      params.endDate = periodFilter.end;
    }

    return params;
  };

  const retrieveOverviews = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    /* Brush, Grid, Chart 초기화 */
    setBrushStartEndIndex({ start: null, end: null });
    setOverviews([]);

    const params = getRequestParams();
    const service = isEqual(VIEWLIST.location, selectedView) ? inventoryOverviewService.getAllByLocation(params) : inventoryOverviewService.getAllByCategory(params);
    service.then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        if (response.data?.currency) {
          currencyMap.current = response.data?.currency;
          for (const result of results) {
            result.currency = CodeUtil.getCurrencySymbol(response.data?.currency?.[result.locationCd]);
          }
        }
        setOverviews(results);
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      })
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: columnDefs(selectedView), viewName, initState: true });
  }

  useEffect(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    if (loading) {
      retrieveOverviews();
      filterValueRef.current = filterValue;
      periodRef.current = periodFilter;
      itemTypeFilterRef.current = itemTypeFilter;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, periodFilter, itemTypeFilter]);

  useEffect(() => {
    if (selectedView && loading) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: columnDefs(selectedView), viewName, initState: true });
      retrieveOverviews();
      reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, selectedView }));
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView]);

  const defaultGridMemo = useMemo(() => { 
    const gridSetting = DefaultGridSetting({ title, viewName });
    
    return gridSetting;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer hasIcons={false}>
        {isEqual(VIEWLIST.location, selectedView) ? 
          <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} enableMultiSelect selectLeafs/>
          : <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        
        }
        <PeriodFilter viewName={viewName} period={periodFilter} setPeriod={setPeriodFilter} dateType={"month"} maxDate={MAX_END_DATE}/>
        <ItemTypeFilter viewName={viewName} itemTypeValue={itemTypeFilter} onChangeFilter={setItemTypeFilter} itemTypeFilters={[ITEM_TYPE.KIT, ITEM_TYPE.FG]}/>
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          defaultValue={VIEWLIST.location}
          value={selectedView}
          name="radio-buttons-group"
          sx={{ display: "block", ml: 1 }}
          onChange={(e) => setSelectedView(e?.target?.value)}
        >
          <FormControlLabel value={VIEWLIST.location} control={<Radio />} label={t(VIEWLIST.location)} />
          <FormControlLabel value={VIEWLIST.category} control={<Radio />} label={t(VIEWLIST.category)} />
        </RadioGroup>
      </FilterContainer>      <Box sx={{ width: "100%", height: "100%" }}>
        <SplitterContainer initialSizes={[1, 1]} collapseDirection='up'>
          <Box className="chart-area" sx={{ height: "100%" }}>
            <ChartComposed
              xAxis={selectXais}
              dataSeries={selectedDataSeries}
              data={overviews}
              allDataSeries={chartSeries}
              options={{ decimalPoint: true }}
              brush
              brushStartEndIndex={brushStartEndIndex}
              setBrushStartEndIndex={setBrushStartEndIndex}
            />
          </Box>
          <Box className="ag-theme-balham grid-area" sx={{ height: "calc(100% - 20px)", mt: 2 }}>
            <AgGridReact
              ref={gridRef}
              rowData={overviews}
              {...defaultGridMemo}
              onGridReady={onGridReady}
            />
          </Box>
        </SplitterContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(InventoryOverview);